import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { GeneralConstants } from 'app/constants';
import { ListOptions, RestfulModel, KnpPaginationData } from 'app/model';
import { Model, ModelPrepped, ModelWithoutId } from 'app/type';

import { BaseApiService } from './base-api.service';
import { RequestParams } from './http-options';

export type IdLike = RestfulModel<number | string> | number | string;

export abstract class RestfulApiService<T extends RestfulModel<number | string>, P = void> extends BaseApiService {
    public createEmptyModel(props?: Partial<T>): ModelWithoutId<T> {
        const defaults = this.getEmptyModelDefaults();

        return {
            ...defaults,
            ...props,
        };
    }

    public listAll(options?: ListOptions, parent?: P, currentData: T[] = []): Observable<T[]> {
        return this.list(options, parent).pipe(
            switchMap((pagination) => {
                if (pagination.data.length < pagination.itemsPerPage) {
                    // reached the end
                    return of([...currentData, ...pagination.data]);
                }

                return this.listAll({ ...options, page: pagination.currentPage + 1 }, parent, [
                    ...currentData,
                    ...pagination.data,
                ]);
            }),
        );
    }

    public list(options?: ListOptions, parent?: P): Observable<KnpPaginationData<T>> {
        const normOptions: ListOptions = {
            page: 1,
            pageSize: GeneralConstants.PAGE_SIZE_SMALL,
            filters: {},
            ...options,
        };

        const params: RequestParams = {
            ...normOptions.extraParams,
            page: normOptions.page,
            filters: normOptions.queryFilters,
            ...normOptions.filters,
        };

        if (normOptions.download) {
            params.download = true;
        }

        if (normOptions.pageSize) {
            params.pageSize = `${normOptions.pageSize}`;
        }

        if (normOptions.sort) {
            params.sort = normOptions.sort;
        }

        return this.http.get<KnpPaginationData<T>>(this.getUrl(parent), { params });
    }

    public findOneBy(filters: Record<string, boolean | number | string>, parent?: P): Observable<T | undefined> {
        return this.list({ filters }, parent).pipe(
            switchMap((data) => {
                if (1 !== data.totalItems) {
                    throw new Error('Non-unique result returned');
                }

                return of(data.data.shift());
            }),
        );
    }

    public get(id: number | string, parent?: P): Observable<Model<T>> {
        return this.http.get<T>(this.getAlterUrl({ id }, parent));
    }

    public save(entity: ModelWithoutId<T>, parent?: P): Observable<Model<T>> {
        if (entity.id) {
            return this.update(entity as T, parent);
        }

        return this.create(entity, parent);
    }

    public create(entity: ModelWithoutId<T>, parent?: P): Observable<Model<T>> {
        const data: Record<string, unknown> = {};
        data[this.getFormName()] = this.prepareSaveData(entity);

        return this.http.post<T>(this.getUrl(parent), data);
    }

    public update(entity: T, parent?: P): Observable<Model<T>> {
        const data: Record<string, unknown> = {};
        data[this.getFormName()] = { ...this.prepareSaveData(entity), id: undefined };

        return this.http.put<T>(this.getAlterUrl(entity, parent), data);
    }

    public patch(entity: IdLike | T, patch: Partial<T>, parent?: P): Observable<Model<T>> {
        const data: Record<string, unknown> = {};

        const sanitized: Record<string, unknown> = patch;
        for (const field in sanitized) {
            if (sanitized[field] === undefined) {
                // for patch we have to explicitly send nulls, otherwise nothing will be unset.
                // eslint-disable-next-line no-null/no-null
                sanitized[field] = null;
            }
        }

        data[this.getFormName()] = sanitized;

        return this.http.patch<T>(this.getAlterUrl(entity, parent), data);
    }

    public delete(entity: T, parent?: P, body?: RequestParams): Observable<void> {
        return this.http.delete(this.getAlterUrl(entity, parent), { body });
    }

    public archive(entity: T, parent?: P): Observable<void> {
        return this.http.get(`${this.getAlterUrl(entity, parent)}/archive`);
    }

    public restore(entity: T, parent?: P): Observable<void> {
        return this.http.get(`${this.getAlterUrl(entity, parent)}/restore`);
    }

    protected getUrl(parent?: P): string {
        return this.getAbsolutePath(this.getPath(parent));
    }

    protected getAlterUrl(entity: IdLike, parent?: P): string {
        if ('object' === typeof entity) {
            return `${this.getUrl(parent)}/${entity.id}`;
        }

        return `${this.getUrl(parent)}/${entity}`;
    }

    /**
     * If data needs to be modified before submission to backend override this method.
     */
    protected prepareSaveData(entity: ModelWithoutId<T>): ModelPrepped<T> {
        return entity;
    }

    /**
     * For service to be able to provide creation functionality, this function needs to be overridden in
     * extending classes.
     */
    protected getEmptyModelDefaults(): ModelWithoutId<T> {
        throw new Error('This service does not support model creation. Please override "getEmptyModelDefaults"');
    }

    protected abstract getFormName(): string;

    protected abstract getPath(parent?: P): string;
}
